// Daily Performance · Sales & Hours panel.
//
// Restaurant selection comes exclusively from the GLOBAL app restaurant
// selector (AppContext) — this component has no local Restaurant
// dropdown. When the global selector is on "all" / unset, the enclosing
// page renders a select-a-restaurant empty state instead of this panel,
// so we can assume `restaurantId` is a specific restaurant here.
//
// Backend behaviour, API endpoints, validation, and audit trail all
// remain identical to the previous implementation.
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useTranslation } from "react-i18next";
import { apiErrorMessage } from "@/lib/apiErrorMessage";

export default function DailyPerformanceSalesHoursPanel({ serviceDate }) {
    const { t } = useTranslation();
    const { restaurantId } = useApp();
    const [d, setD] = useState(
        serviceDate || (() => new Date().toISOString().slice(0, 10)));
    const [gross, setGross] = useState("");
    const [net, setNet] = useState("");
    const [cust, setCust] = useState("");
    const [hours, setHours] = useState("");
    // Core integration V1 — automatic hours (read-only) from the
    // restaurant's authorized start date; null = manual entry.
    const [auto, setAuto] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState("");

    // If the enclosing page owns the date (e.g. shared with Waste),
    // keep the internal state in sync when it changes.
    useEffect(() => {
        if (serviceDate && serviceDate !== d) setD(serviceDate);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serviceDate]);

    const loadDay = async () => {
        if (!restaurantId || !d) { setEditingId(null); return; }
        try {
            const { data } = await api.get("/corporate/daily-performance",
                { params: { restaurant_id: restaurantId, from: d, to: d } });
            const hit = (data.items || []).find((r) => r.date === d);
            const af = data.automatic_hours_from;
            setAuto(af && d >= af ? (data.automatic_hours?.[d] || null) : null);
            if (hit) {
                setEditingId(hit.id);
                setGross(String(hit.gross_sales ?? ""));
                setNet(String(hit.net_sales ?? ""));
                setCust(String(hit.customers ?? ""));
                setHours(String(hit.worked_hours ?? ""));
            } else {
                setEditingId(null);
                setGross(""); setNet(""); setCust(""); setHours("");
            }
        } catch { /* silent */ }
    };
    useEffect(() => { loadDay(); /* eslint-disable-next-line */ },
        [restaurantId, d]);

    const save = async () => {
        if (!restaurantId || !d) return;
        setSaving(true); setMsg("");
        try {
            const body = {
                restaurant_id: restaurantId, date: d,
                gross_sales: Number(gross || 0),
                net_sales: Number(net || 0),
                customers: parseInt(cust || "0", 10),
                worked_hours: Number(hours || 0),
            };
            if (auto) delete body.worked_hours;
            if (editingId) {
                await api.patch(`/corporate/daily-performance/${editingId}`, {
                    gross_sales: body.gross_sales,
                    net_sales: body.net_sales,
                    customers: body.customers,
                    ...(auto ? {} : { worked_hours: body.worked_hours }),
                });
            } else {
                await api.post("/corporate/daily-performance", body);
            }
            setMsg(t("dpPanel.saved", "Saved."));
            await loadDay();
        } catch (e) {
            setMsg(t("dpPanel.couldNotSave", { defaultValue: "Could not save: {{detail}}",
                detail: apiErrorMessage(e) || t("dpPanel.error", "error") }));
        } finally { setSaving(false); }
    };

    // Teho display · read-only calculated preview. Corporate Reports
    // still compute the authoritative Teho on the backend.
    const teho = (() => {
        const n = Number(net); const h = auto ? auto.worked_hours : Number(hours);
        if (!Number.isFinite(n) || !Number.isFinite(h) || h <= 0) return null;
        return n / h;
    })();

    return (
        <section className="mb-6 rounded-2xl border border-neutral-200/70 bg-white p-6"
                 data-testid="daily-performance-sales-hours-panel">
            {!serviceDate && (
                <div className="mb-6">
                    <label className="block text-[11px] font-medium text-neutral-500 mb-1.5">
                        {t("dpPanel.date", "Date")}
                    </label>
                    <input type="date" data-testid="dp-date"
                        value={d} onChange={(e) => setD(e.target.value)}
                        className="h-10 px-3 border border-neutral-200 rounded-xl text-sm bg-white" />
                </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                    <label className="block text-[11px] font-medium text-neutral-500 mb-1.5">
                        {t("dpPanel.gross", "Gross Sales (€)")}
                    </label>
                    <input type="number" step="0.01" min="0" data-testid="dp-gross"
                        value={gross} onChange={(e) => setGross(e.target.value)}
                        className="h-10 w-full px-3 border border-neutral-200 rounded-xl text-sm" />
                </div>
                <div>
                    <label className="block text-[11px] font-medium text-neutral-500 mb-1.5">
                        {t("dpPanel.net", "Net Sales (€)")}
                    </label>
                    <input type="number" step="0.01" min="0" data-testid="dp-net"
                        value={net} onChange={(e) => setNet(e.target.value)}
                        className="h-10 w-full px-3 border border-neutral-200 rounded-xl text-sm" />
                </div>
                <div>
                    <label className="block text-[11px] font-medium text-neutral-500 mb-1.5">
                        {t("dpPanel.customers", "Customers")}
                    </label>
                    <input type="number" step="1" min="0" data-testid="dp-customers"
                        value={cust} onChange={(e) => setCust(e.target.value)}
                        className="h-10 w-full px-3 border border-neutral-200 rounded-xl text-sm" />
                </div>
                <div>
                    <label className="block text-[11px] font-medium text-neutral-500 mb-1.5">
                        {t("dpPanel.workedHours", "Worked Hours")}
                    </label>
                    {auto ? (
                        <div data-testid="dp-worked-hours-automatic" aria-readonly="true"
                            className="min-h-10 w-full px-3 py-2 border border-neutral-100 bg-neutral-50 rounded-xl text-sm">
                            <span className="font-medium">{auto.worked_hours.toFixed(2)} h</span>
                            <span className="block text-[11px] text-neutral-500"
                                data-testid="dp-worked-hours-split">
                                {t("dpPanel.automatic", { defaultValue: "Automatic: employees {{emp}} h + extras {{ext}} h", emp: auto.employee_hours.toFixed(2), ext: auto.external_hours.toFixed(2) })}
                                {auto.complete ? "" : t("dpPanel.notComplete", " · not complete yet")}
                            </span>
                        </div>
                    ) : (
                        <input type="number" step="0.1" min="0" data-testid="dp-worked-hours"
                            value={hours} onChange={(e) => setHours(e.target.value)}
                            className="h-10 w-full px-3 border border-neutral-200 rounded-xl text-sm" />
                    )}
                </div>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-4">
                <div className="rounded-xl bg-neutral-50 border border-neutral-100 px-4 py-2.5
                                 text-sm tabular-nums flex items-center gap-3">
                    <span className="text-[11px] uppercase tracking-[0.14em] font-semibold text-neutral-500">
                        {t("dpPanel.teho", "Teho (€ / h)")}
                    </span>
                    <span data-testid="dp-teho-readout" aria-readonly="true">
                        {teho == null ? "—" : `€ ${teho.toFixed(2)} / h`}
                    </span>
                    {auto && !auto.complete && teho != null && (
                        <span data-testid="dp-teho-not-final" className="text-[11px] text-amber-700">
                            {t("dpPanel.notFinal", "not final — hours incomplete")}
                        </span>
                    )}
                </div>
                <div className="ml-auto flex items-center gap-3">
                    {editingId && (
                        <span data-testid="dp-editing-hint"
                              className="text-xs text-neutral-500">
                            {t("dpPanel.editingHint", "Editing existing record — audited per changed field.")}
                        </span>
                    )}
                    {msg && (
                        <span data-testid="dp-msg"
                              className="text-xs text-neutral-500">{msg}</span>
                    )}
                    <button data-testid="dp-save"
                        onClick={save} disabled={!restaurantId || !d || saving}
                        className="h-10 px-5 shrink-0 whitespace-nowrap rounded-full bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-800 disabled:opacity-50">
                        {saving ? t("dpPanel.saving", "Saving…") : editingId ? t("dpPanel.updateDay", "Update day") : t("dpPanel.saveDay", "Save day")}
                    </button>
                </div>
            </div>
        </section>
    );
}
